import * as THREE from "three";
import { getActiveAttachmentPoints } from "../../utils/geometry.js";
import { AP_MOUNTED_COMPONENT_TYPES, isFreePlaceable } from "../../utils/constants.js";

export const placementGhostMethods = {
  updateGhostFurniturePreview(raycaster) {
    const furnitureManager = this.app.getFurnitureManager();

    // While dragging existing furniture, the drag ghost IS the furnitureManager's
    // ghost (see createDragGhost) and the drag path already drives its snap. Running
    // this placement preview too would fire a SECOND snap search at the ghost's
    // (auto-rotated) rotation, competing with the drag search at the intended
    // rotation — two searches per frame fighting over the chosen face. Skip it.
    // (Gated on an active existing-furniture drag so catalog placement is untouched.)
    if (this.isDragging && (furnitureManager?.getSelectedFurnitureArray()?.length > 0)) return;

    const ghostFurniture = furnitureManager?.getGhostFurniture();

    if (!ghostFurniture || !ghostFurniture.isVisible) return;

    // Check if ghost furniture is a module and should snap to nearby modules
    const ghostComponentType = ghostFurniture.furnitureData?.componentType;

    const newPosition = this._cursorHit(raycaster, AP_MOUNTED_COMPONENT_TYPES.has(ghostComponentType));
    if (!newPosition) return;

    if (newPosition) {
      let snappedToAttachment = false;
      let closestAttachment = null;

      if (ghostComponentType === "module") {
        // Snap SELECTION uses a stable rotation captured per placement, NOT the live
        // ghost rotation — the ghost is auto-rotated each frame to fit the snapped face
        // (updateGhostFurnitureRotation below), and reading that back makes the chosen
        // face oscillate. Re-capture only when the ghost instance changes (new
        // placement); R-key rotation updates it explicitly (see KeyR handler).
        if (this._ghostRotRef !== ghostFurniture) {
          this._ghostRotRef = ghostFurniture;
          this.ghostSelectRotation = ghostFurniture.getCurrentRotation();
        }
        const currentGhostRotation = this.ghostSelectRotation;

        // Find nearby attachment points that accept modules
        const ghostAttachmentPoints = getActiveAttachmentPoints(ghostFurniture.furnitureData, 0);
        const ghostSpecialCase = ghostFurniture.furnitureData?.properties?.specialCaseSnapping === true;
        closestAttachment = this.findClosestModuleAttachment(newPosition, null, ghostAttachmentPoints, currentGhostRotation, ghostSpecialCase);

        if (closestAttachment) {

          // Calculate snapped position. Pass the live cursor pose (newPosition) so the
          // connector-distance tiebreak matches the ranking used to pick this target.
          const snapResult = this.calculateSnapPosition(closestAttachment, ghostAttachmentPoints, currentGhostRotation, newPosition);

          if (snapResult) {
            // Validity = within room bounds AND no collision with other modules.
            // Exclude the parent's entire connected group — same reasoning as the
            // drag-snap case: AABB of a long module (e.g. E3) would incorrectly
            // overlap modules on the other side of an R corner piece.
            const clamped = this.clampGhostPosition(ghostFurniture, snapResult.position);
            const boundsValid =
              Math.abs(clamped.x - snapResult.position.x) < 0.01 &&
              Math.abs(clamped.z - snapResult.position.z) < 0.01;
            const parentExclude = new Set([...closestAttachment.parentFurniture.getAllGroupModules()]);
            furnitureManager.updateGhostFurniturePosition(snapResult.position.x, snapResult.position.y, snapResult.position.z);
            furnitureManager.updateGhostFurnitureRotation(snapResult.rotation);
            ghostFurniture.getMesh().parent?.updateMatrixWorld(true); // update from parent down
            const snapWorldPos = ghostFurniture.getMesh().getWorldPosition(new THREE.Vector3());
            const snapHasCollision = this.checkGhostFurnitureCollisions(ghostFurniture, snapWorldPos, parentExclude);
            const snapIsValid = boundsValid && !snapHasCollision &&
              !this._snapPenetratesGroup(ghostFurniture, snapResult, closestAttachment);

            if (snapIsValid) {
              // Only snap when valid — invalid snaps fall through so the ghost
              // follows the cursor and renders red there instead of snapping to
              // an invalid position.
              closestAttachment.setHighlight(true, true);
              // furnitureManager.updateGhostFurniturePosition(snapResult.position.x, snapResult.position.y, snapResult.position.z);
              // furnitureManager.updateGhostFurnitureRotation(snapResult.rotation);
              ghostFurniture.setSnapped(true, closestAttachment, snapResult.childAttachmentSide, true);
              snappedToAttachment = true;
            }
            // Invalid snap: fall through — non-snapped branch keeps ghost at cursor and shows red
          }
        }
      } else if (AP_MOUNTED_COMPONENT_TYPES.has(ghostComponentType)) {
        // Component ghost: snap to nearest compatible AP, with sliding + collision
        const prevTarget = this.componentDragTarget;
        closestAttachment = this._findComponentSnapTarget(newPosition, ghostFurniture.furnitureData);
        if (prevTarget && prevTarget !== closestAttachment) prevTarget.setHighlight(false);

        if (closestAttachment) {
          const slideLocal = this._resolveComponentSnapPose(closestAttachment, ghostFurniture.furnitureData);
          this.componentDragSlidOffset = slideLocal ? slideLocal.clone() : null;

          furnitureManager.updateGhostFurniturePosition(this._componentSnapPos.x, this._componentSnapPos.y, this._componentSnapPos.z);
          ghostFurniture.getMesh().quaternion.copy(this._componentSnapQuat);

          const parentFurniture = closestAttachment.parentFurniture;
          const compValid = parentFurniture.shouldShowAttachmentPoint(closestAttachment) &&
            !this.checkComponentCollision(ghostFurniture, parentFurniture, null, closestAttachment);
          closestAttachment.setHighlight(true, compValid);
          ghostFurniture.setSnapped(true, closestAttachment, null, compValid);
          snappedToAttachment = true;
          this.componentDragTarget = closestAttachment;
        } else {
          this.componentDragTarget = null;
          this.componentDragSlidOffset = null;
        }
      }

      if (!snappedToAttachment) {
        if (ghostFurniture.snappedAttachmentPoint) {
          ghostFurniture.snappedAttachmentPoint.setHighlight(false);
        }

        const freePlaceable = isFreePlaceable(ghostComponentType);
        const clampedPosition = this.clampGhostPosition(ghostFurniture, newPosition);
        furnitureManager.updateGhostFurniturePosition(clampedPosition.x, clampedPosition.y, clampedPosition.z);
        // For a free (un-snapped) ghost, revert to the intended rotation rather
        // than the leftover auto-rotated snap orientation from a previous frame.
        const currentRotation = (freePlaceable && this._ghostRotRef === ghostFurniture && this.ghostSelectRotation != null)
          ? this.ghostSelectRotation
          : ghostFurniture.getCurrentRotation();
        furnitureManager.updateGhostFurnitureRotation(currentRotation);

        let placementIsValid = true;
        if (freePlaceable) {
          // If we tried a snap and it was invalid, show red at cursor position
          const triedInvalidSnap = closestAttachment !== null;
          if (triedInvalidSnap) {
            placementIsValid = false;
          } else {
            placementIsValid = !this.checkGhostFurnitureCollisions(ghostFurniture, clampedPosition);
          }
        } else {
          // Components other than accessories can only exist on an AP
          placementIsValid = false;
        }
        ghostFurniture.setSnapped(false, null, null, placementIsValid);
      }
    }
  },
};
